<!--
# -----------------------------------------------------------------------------
# Component: Payment.vue
# Author: antonio.ramirez@pst.sg
# Created Date: 06/May/2019
# Comments: Manage Payment Module
# -----------------------------------------------------------------------------
-->
<template>
  <page :load="doInitPayment">
    <v-container fluid grid-list-md>
      <v-layout row wrap align-end>
        <v-flex xs7 md9 lg10>
          <card-menu :items="cardMenu" @cardReturn="visibleControl" subMenu></card-menu>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12>
          <form data-vv-scope="paymentFilterForm">
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn class="mx-2" fab dark small color="teal" v-on="on" @click.native.stop="showFilterList = true">
                          <v-icon dark>mdi-format-list-bulleted</v-icon>
                        </v-btn>
                      </template>
                      <span>Show Saved Filters</span>
                    </v-tooltip>
                    Filters for Payment
                    <span class="caption grey--text font-weight-light">
                      {{ defaultFilter.filterName !== undefined ? ' (' + defaultFilter.filterName + ')' : '' }}</span
                    >
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex xs6 sm4 md3 class="lg4-custom">
                            <v-autocomplete
                              :items="branches"
                              v-model="paymentForm.branch"
                              :label="paymentFormLabel.branch"
                              item-text="label"
                              item-value="value"
                              data-vv-name="paymentForm.branch"
                              :data-vv-as="paymentFormLabel.branch"
                              :error-messages="errors.collect('paymentForm.branch')"
                              clearable
                            ></v-autocomplete>
                          </v-flex>
                          <v-flex xs6 sm4 md3 class="lg4-custom">
                            <v-text-field
                              v-model="paymentForm.accountNo"
                              :label="paymentFormLabel.accountNo"
                              :error-messages="errors.collect('accountNo')"
                              data-vv-name="accountNo"
                              :data-vv-as="paymentFormLabel.accountNo"
                              v-validate="'entry|max:60'"
                            >
                              <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                <v-icon color="info" @click="showAcctNoHelpList = true">mdi-magnify</v-icon>
                              </v-slide-x-reverse-transition>
                            </v-text-field>
                          </v-flex>
                          <v-flex xs6 sm4 md3 class="lg4-custom">
                            <v-text-field
                              v-model="paymentForm.documentNo"
                              :label="paymentFormLabel.documentNo"
                              :error-messages="errors.collect('documentNo')"
                              data-vv-name="documentNo"
                              :data-vv-as="paymentFormLabel.documentNo"
                              v-validate="'entry|max:60'"
                            >
                              <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                <v-icon color="info" @click="showDocNoHelpList = true">mdi-magnify</v-icon>
                              </v-slide-x-reverse-transition>
                            </v-text-field>
                          </v-flex>
                          <v-flex xs6 sm4 md3 class="lg4-custom">
                            <v-text-field
                              v-model="paymentForm.currency"
                              :label="paymentFormLabel.currency"
                              :error-messages="errors.collect('currency')"
                              data-vv-name="currency"
                              :data-vv-as="paymentFormLabel.currency"
                              v-validate="'entry|max:60'"
                            >
                              <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                <v-icon color="info" @click="showCurrencyHelpList = true">mdi-magnify</v-icon>
                              </v-slide-x-reverse-transition>
                            </v-text-field>
                          </v-flex>
                          <v-flex xs6>
                            <cal-picker
                              v-model="paymentForm.transactionDateFrom"
                              :label="paymentFormLabel.transactionDateFrom"
                              :data-vv-as="paymentFormLabel.transactionDateFrom"
                              data-vv-name="transactionDateFrom"
                            ></cal-picker>
                          </v-flex>
                          <v-flex xs6>
                            <cal-picker
                              v-model="paymentForm.transactionDateTo"
                              :label="paymentFormLabel.transactionDateTo"
                              :data-vv-as="paymentFormLabel.transactionDateTo"
                              data-vv-name="transactionDateTo"
                            ></cal-picker>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                    <v-layout row wrap>
                      <v-spacer></v-spacer>
                      <v-card-actions>
                        <v-btn color="red darken-1" text @click.native="doClearFilters">Clear Filters</v-btn>
                        <v-btn color="primary" text @click.native="saveFilter = true">Save Current Filters</v-btn>
                        <v-btn color="primary" text type="submit" @click.prevent="doFilters">Apply Filters</v-btn>
                      </v-card-actions>
                    </v-layout>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </form>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card>
            <v-flex class="text-right">
              <v-btn class="mr-2" color="primary" @click.native="doMultiplePayment" v-if="checkAccess([permissionKeys.perm_041070])">
                <span>{{ lblMultiplePayment }}</span>
                <v-icon right dark>mdi-plus</v-icon>
              </v-btn>
              <v-btn color="primary" @click.native="doCreatePayment" v-if="checkAccess([permissionKeys.perm_041075])">
                <span>{{ lblCreatePayment }}</span>
                <v-icon right dark>mdi-plus</v-icon>
              </v-btn>
              <v-btn class="ml-2" v-if="checkAccess([permissionKeys.perm_041245])" color="info" @click.native="doExportFilters">
                <span>{{ lblExportList }}</span>
                <v-icon right dark>mdi-file-excel</v-icon>
              </v-btn>
            </v-flex>
            <v-flex xs12>
              <v-card>
                <v-data-table
                  v-bind:headers="listPaymtHeaders"
                  v-bind:items="items"
                  :server-items-length="totalItems"
                  :footer-props="{'items-per-page-options': itemsPerPage }"
                  v-bind:options.sync="pagination"
                  item-key="key"
                  :loading="loading"
                  :no-data-text="noDataText"
                >
                  <template v-slot:item="row"
                    ><tr>
                      <td v-for="column in selectedPaymentColumns" :key="column">{{ row.item[column] }}</td>
                      <td class="table-action-header">
                        <table-action
                          :rowData="row"
                          :query="checkAccess([permissionKeys.perm_041065])"
                          @query="doQuery(row)"
                          :review="checkAccess([permissionKeys.perm_041060])"
                          @review="doReview(row)"
                          :remove="canRemove(row.item) && checkAccess([permissionKeys.perm_041055])"
                          @remove="doPromptRemove(row.item)"
                        >
                        </table-action>
                      </td></tr
                  ></template>
                </v-data-table>
              </v-card>
            </v-flex>
          </v-card>
        </v-flex>
        <v-dialog v-model="showErrorDialog" width="800px">
          <v-card>
            <v-toolbar dense color="grey lighten-3" flat>
              <v-icon>mdi-information</v-icon>
              <v-toolbar-title>System Information</v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
              <div v-if="messages.length > 0" class="pb-3">
                <v-subheader>Oops something went wrong:</v-subheader>
                <ul class="showErrorMsgs">
                  <li v-for="(error, index) in messages" :key="index">
                    {{ error.message }}
                  </li>
                </ul>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="showBranchHelpList" width="1280px">
          <branch-help-list @close="showBranchHelpList = false" @assign-branch="assignBranch"></branch-help-list>
        </v-dialog>
        <v-dialog v-model="showAcctNoHelpList" width="1280px">
          <cr-acct-no-help-list @close="showAcctNoHelpList = false" @assign-acct-no="assignAcctNo"></cr-acct-no-help-list>
        </v-dialog>
        <v-dialog v-model="showDocNoHelpList" width="1280px">
          <paymt-doc-no-help-list @close="showDocNoHelpList = false" @assign-doc-no="assignDocNo"></paymt-doc-no-help-list>
        </v-dialog>
        <v-dialog v-model="showCurrencyHelpList" width="1280px">
          <cr-currency-help-list @close="showCurrencyHelpList = false" @assign-currency="assignCurr"></cr-currency-help-list>
        </v-dialog>
        <v-dialog v-model="showFilterList" width="800px" persistent>
          <SearchFilter
            :boolRefresh="showFilterList"
            :filterTitle="'Saved Filters - Payment Listing'"
            @close="showFilterList = false"
            :filterCode="filterCode"
            @applyDefaultFilter="applyDefaultFilter"
          ></SearchFilter>
        </v-dialog>
        <v-dialog v-model="saveFilter" width="800px" persistent>
          <UpdateSearchFilter
            :boolRefresh="saveFilter"
            :dialogTitle="'Filter'"
            :actionName="'new'"
            :filters="paymentForm"
            @close="saveFilter = false"
            @reloadList="reloadList"
            :filterCode="filterCode"
            @applyDefaultFilter="applyDefaultFilter"
          ></UpdateSearchFilter>
        </v-dialog>
      </v-layout>
    </v-container>

    <warning v-model="exportList.promptFilterWarning" :label="exportList.promptFilterWarningMsg" @ok="doExportList()"/>
  </page>
</template>

<script>
import routeMixin from '@/mixins/route'
import MessageMixin from '@/mixins/message'
import { paymentListMixin } from '@/mixins/accounting/payment/paymentListMixin'
import { paymentFilterMixin } from '@/mixins/accounting/payment/paymentFilterMixin'
import { paymentAjaxMixin } from '@/mixins/accounting/payment/paymentAjaxMixin'
import BranchHelpList from '@/components/core/common/dialog/BranchHelpList'
import CrAcctNoHelpList from '@/components/core/common/dialog/CrAcctNoHelpList'
import PaymtDocNoHelpList from '@/components/core/common/dialog/PaymtDocNoHelpList'
import CrCurrencyHelpList from '@/components/core/common/dialog/CrCurrencyHelpList'
import { PAYMENT_LOAD_DATA, CHG_ACCT_MCH_RECKEY } from '@/store/types'
import { mapGetters } from 'vuex'
import SearchFilter from '@/components/core/common/dialog/SearchFilter'
import UpdateSearchFilter from '@/components/core/common/dialog/UpdateSearchFilter'
import { expandURI } from '@/utils/ajax'
import { recordLockMixin } from '@/mixins/common/recordLockMixin'
import { permissionKeys } from '@/mixins/global/permissionKey'
import { strJoin } from '@/utils/stringutil'

export default {
  data() {
    return {
      permissionKeys: permissionKeys,
      lblCreatePayment: 'New Payment',
      lblMultiplePayment: 'Enter Multiple Accounts for Payment',
      lblExportList: 'Export List',
      cardMenu: [
        { text: 'Accounts', icon: 'mdi-file-document-box', name: 'accounts', params: { tab: 'accounts' }, access: '', default: false },
        { text: 'Receipt', icon: 'mdi-receipt', name: 'receipts', params: { tab: 'receipt' }, access: '', default: false },
        { text: 'Payment', icon: 'mdi-cash', name: 'payment', params: { tab: 'payment' }, access: '', default: true },
        { text: 'Credit Note', icon: 'mdi-credit-card', name: 'creditNote', params: { tab: 'creditNote' }, access: '', default: false },
        { text: 'Debit Note', icon: 'mdi-credit-card-multiple', name: 'debitNote', params: { tab: 'debitNote' }, access: '', default: false },
        { text: 'Non-Trade', icon: 'mdi-credit-card-multiple', name: 'nonTrade', params: { tab: 'nonTrade' }, access: '', default: false }
      ],
      paymentData: {
        tranMode: '',
        branch: '',
        accountNo: '',
        type: '',
        documentNo: '',
        fund: '',
        suffix: ''
      },
      filterCode: 'PAY',
      newFilter: false,
      saveFilter: false,
      showFilterList: false,
      defaultFilter: {},
      showErrorDialog: false,
      pagination: {
        sortDesc: [false],
        page: 1
      },
      exportList: {
        promptFilterWarning: false,
        promptFilterWarningMsg: 'Do you want to proceed without any filtering?'
      }
    }
  },
  computed: {
    ...mapGetters(['checkAccess']),
    initPayment() {
      return this.$store.state.payment.init
    },
    displayErrorDialog() {
      return this.messages
    }
  },
  watch: {
    displayErrorDialog: {
      handler: function(messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    }
  },
  methods: {
    visibleControl(data) {
      if (typeof data !== 'undefined') {
        this.visibleTab = { ...data }
      } else {
        this.visibleTab = {}
      }
    },
    async doInitPayment() {
      await Promise.all([this.$store.dispatch('paymentInit')])
      await Promise.all([this.$store.dispatch('searchFilterInit')])
      sessionStorage.removeItem('matchList') // remove match List session
      this.getDefaultFilter()
      this.$store.commit(PAYMENT_LOAD_DATA, this.paymentData)
      this.cardMenu = []
      if (this.checkAccess([permissionKeys.perm_041005]) === true) {
        this.cardMenu.push({
          text: 'Accounts',
          icon: 'mdi-file-document-box',
          name: 'accounts',
          params: { tab: 'accounts' },
          access: '',
          default: false
        })
      }
      if (this.checkAccess([permissionKeys.perm_041015]) === true) {
        this.cardMenu.push({ text: 'Receipt', icon: 'mdi-receipt', name: 'receipts', params: { tab: 'receipt' }, access: '', default: false })
      }
      if (this.checkAccess([permissionKeys.perm_041050]) === true) {
        this.cardMenu.push({ text: 'Payment', icon: 'mdi-cash', name: 'payment', params: { tab: 'payment' }, access: '', default: true })
      }
      if (this.checkAccess([permissionKeys.perm_041085]) === true) {
        this.cardMenu.push({
          text: 'Credit Note',
          icon: 'mdi-credit-card',
          name: 'creditNote',
          params: { tab: 'creditnote' },
          access: '',
          default: false
        })
      }
      if (this.checkAccess([permissionKeys.perm_041115]) === true) {
        this.cardMenu.push({
          text: 'Debit Note',
          icon: 'mdi-credit-card-multiple',
          name: 'debitNote',
          params: { tab: 'debitnote' },
          access: '',
          default: false
        })
      }
      if (this.checkAccess([permissionKeys.perm_041210]) === true) {
        this.cardMenu.push({
          text: 'Non-Trade',
          icon: 'mdi-credit-card-multiple',
          name: 'nonTrade',
          params: { tab: 'nontrade' },
          access: '',
          default: false
        })
      }
      this.fetchBranchWBranchUserId()
    },
    doReview(data) {
      // AR: OS-660 Added to check if record is locked 2021-01-29
      let recKey = strJoin('|', data.item.type, data.item.branch, data.item.accountNo, data.item.documentNo, data.item.fund, data.item.suffix)
      this.$store.commit(CHG_ACCT_MCH_RECKEY, recKey)
      this.checkRecordLock(recKey, 'ACT').then(result => {
        // Record not locked proceed to review 2021-01-29
        if (!result) {
          this.performReview(data)
          // Record was locked show message 2021-01-29
        } else {
          this.loading = false
        }
      })
    },
    performReview(data) {
      if (data.item) {
        data.item.tranMode = 'R'
        this.$store.commit(PAYMENT_LOAD_DATA, data.item)
        this.$router.push({ name: 'createPayment' }).catch(() => {})
      }
    },
    doQuery(data) {
      if (data.item) {
        data.item.tranMode = 'Q'
        this.$store.commit(PAYMENT_LOAD_DATA, data.item)
        this.$router.push({ name: 'queryPayment' }).catch(() => {})
      }
    },
    doPromptRemove(data) {
      if (data.item) {
        // this.$router.push({ name: 'createPayment', params: { paymentData: data.item } })
      }
    },
    doCreatePayment() {
      this.$router.push({ name: 'createPayment' }).catch(() => {})
    },
    doMultiplePayment() {
      this.$router.push({ name: 'mulAcctPayment' }).catch(() => {})
    },
    assignBranch(branch) {
      this.paymentForm.branch = branch
      this.showBranchHelpList = false
    },
    assignAcctNo(acctNo) {
      this.paymentForm.accountNo = acctNo
      this.showAcctNoHelpList = false
    },
    assignDocNo(docNo) {
      this.paymentForm.documentNo = docNo
      this.showDocNoHelpList = false
    },
    assignCurr(curr) {
      this.paymentForm.currency = curr
      this.showCurrencyHelpList = false
    },
    reloadList(filterName) {
      this.defaultFilter.filterName = filterName
      this.saveFilter = false
      this.performSearch()
    },
    applyDefaultFilter(filterData) {
      this.defaultFilter = filterData
      if (this.defaultFilter.filters !== undefined) {
        this.paymentForm = this.defaultFilter.filters
      }
      this.showFilterList = false
      this.performSearch(false)
    },
    async getDefaultFilter() {
      const apiUrl = this.$store.state.searchFilter.menu.defaultFilter
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            filterCode: this.filterCode
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          this.initialLoad = false
          if (data) {
            this.defaultFilter.filterName = data.defaultFilter.filterName
            this.defaultFilter.filterDesc = data.defaultFilter.filterDesc
            this.defaultFilter.filterCode = data.defaultFilter.filterCode
            this.defaultFilter.filters = JSON.parse(data.defaultFilter.filterContent)
            if (this.defaultFilter.filters !== undefined) {
              this.paymentForm = this.defaultFilter.filters
            }
          }
          this.firstTimeCalling = false
          this.performSearch(false)
        } catch (err) {
          this.loading = false
        }
      }
    },
    canRemove(data) {
      if (data.postNow === 'N' && data.void_ === false) {
        return true
      }
      return false
    }
  },
  mounted() {
    // AR: OS-660 Added to check record lock 2021-01-21
    this.initCommonUtilMenu()
  },
  mixins: [routeMixin, MessageMixin, paymentListMixin, paymentAjaxMixin, paymentFilterMixin, recordLockMixin],
  components: {
    BranchHelpList,
    CrAcctNoHelpList,
    PaymtDocNoHelpList,
    CrCurrencyHelpList,
    SearchFilter,
    UpdateSearchFilter
  }
}
</script>
<style lang="scss">
@import '../../../../assets/scss/accounting.scss';
</style>
